// ============================================================================
// Centralized SEO Configuration, Structured Data (JSON-LD), Sitemap & Pre-render
// ============================================================================

export const CANONICAL_ORIGIN = 'https://maintenanceprep.app';
export const DEFAULT_OG_IMAGE = `${CANONICAL_ORIGIN}/og-image.png`;
export const LAST_MODIFIED_DATE = '2026-10-21';

export interface SeoRouteConfig {
  path: string;
  canonicalUrl: string;
  title: string;
  description: string;
  keywords: string;
  robots: string;
  ogType: 'website' | 'article';
  ogImage: string;
  ogImageAlt: string;
  indexable: boolean;
  priority: string;
  changefreq: 'daily' | 'weekly' | 'monthly';
  jsonLd: Record<string, unknown>;
  prerenderedHtml: string;
}

const SHARED_ORGANIZATION_NODE = {
  '@type': 'EducationalOrganization',
  '@id': `${CANONICAL_ORIGIN}/#organization`,
  name: 'MaintenancePrep',
  url: `${CANONICAL_ORIGIN}/`,
  logo: {
    '@type': 'ImageObject',
    url: `${CANONICAL_ORIGIN}/icon-512.png`,
    width: 512,
    height: 512,
  },
  image: DEFAULT_OG_IMAGE,
  description:
    'Independent industrial and postal maintenance exam preparation platform covering Ramsay Maintenance Assessments (MEC-TEST, ELE-TEST, MultiCrafTest, MAT) and USPS Postal Exam 955.',
};

const SHARED_WEBSITE_NODE = {
  '@type': 'WebSite',
  '@id': `${CANONICAL_ORIGIN}/#website`,
  url: `${CANONICAL_ORIGIN}/`,
  name: 'MaintenancePrep',
  description: 'Ramsay & USPS Maintenance Exam 955 Practice Tests, Schematics & Simulators',
  publisher: {
    '@id': `${CANONICAL_ORIGIN}/#organization`,
  },
  inLanguage: 'en-US',
};

const SHARED_NAV_HTML = `
  <header>
    <nav aria-label="Primary Navigation">
      <a href="/">MaintenancePrep Overview</a>
      <a href="/track/mechanical">Mechanical Maintenance (MEC-TEST)</a>
      <a href="/track/electrical">Electrical &amp; Electronics (ELE-TEST)</a>
      <a href="/track/multicraft">Multicraft &amp; USPS Exam 955</a>
      <a href="/track/aptitude">Mechanical Aptitude &amp; Spatial (MAT)</a>
      <a href="/course/schematics">Electrical Schematics Course</a>
      <a href="/study-lab">Interactive Study Lab &amp; Calculators</a>
      <a href="/pricing">Pricing &amp; 14-Day Free Trial</a>
    </nav>
  </header>
`;

const SHARED_FOOTER_HTML = `
  <footer>
    <nav aria-label="Assessment Tracks &amp; Resources">
      <ul>
        <li><a href="/track/mechanical">Mechanical Maintenance (MEC-TEST)</a></li>
        <li><a href="/track/electrical">Electrical &amp; Electronics (ELE-TEST)</a></li>
        <li><a href="/track/multicraft">Multicraft &amp; USPS Exam 955</a></li>
        <li><a href="/track/aptitude">Mechanical Aptitude &amp; Spatial (MAT)</a></li>
        <li><a href="/course/schematics">Electrical Schematics &amp; Blueprint Reading Course</a></li>
        <li><a href="/study-lab">Industrial Maintenance Study Lab, Flashcards &amp; Formula Calculators</a></li>
        <li><a href="/pricing">MaintenancePrep Pro 14-Day Free Trial</a></li>
      </ul>
    </nav>
    <p>MaintenancePrep is an independent educational preparation platform and is not affiliated with, sponsored by, or endorsed by Ramsay Corporation or the United States Postal Service (USPS).</p>
  </footer>
`;

function createBreadcrumbNode(items: Array<{ name: string; path: string }>) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.name,
      item: item.path === '/' ? `${CANONICAL_ORIGIN}/` : `${CANONICAL_ORIGIN}${item.path}`,
    })),
  };
}

export function normalizeSeoPath(rawPath: string): string {
  if (!rawPath) return '/';
  let cleaned = rawPath.trim();
  if (cleaned.startsWith('#')) {
    cleaned = cleaned.slice(1);
  }
  cleaned = cleaned.split('?')[0].split('#')[0];
  if (!cleaned.startsWith('/')) {
    cleaned = '/' + cleaned;
  }
  if (cleaned.length > 1 && cleaned.endsWith('/')) {
    cleaned = cleaned.slice(0, -1);
  }

  // Canonical route aliases
  if (cleaned === '/overview' || cleaned === '/index.html') return '/';
  if (
    cleaned === '/schematics' ||
    cleaned === '/schematics-course' ||
    cleaned === '/schematic-course'
  ) {
    return '/course/schematics';
  }
  if (cleaned === '/flashcards' || cleaned === '/formulas') {
    return '/study-lab';
  }

  return cleaned;
}

export const SEO_ROUTES: Record<string, SeoRouteConfig> = {
  '/': {
    path: '/',
    canonicalUrl: `${CANONICAL_ORIGIN}/`,
    title: 'MaintenancePrep | Ramsay & USPS Maintenance Exam 955 Practice Tests',
    description:
      'Prepare for Ramsay Maintenance Assessments (MEC-TEST, ELE-TEST, MultiCrafTest, MAT) and USPS Postal Exam 955 (MM-7, MPE-9, BEM-9, ET-10) with interactive practice tests, ANSI/IEEE circuit schematics, and realistic 60-minute timed mocks.',
    keywords:
      'Ramsay Maintenance Assessment, Ramsay test practice, MEC-TEST, ELE-TEST, MultiCrafTest, Ramsay mechanical aptitude test, Ramsay MAT, USPS Exam 955, Postal Exam 955 practice test, Maintenance Mechanic MM-7, MPE-9, BEM-9, Electronic Technician ET-10, industrial maintenance test, multicraft test questions, electrical maintenance exam',
    robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'MaintenancePrep — Pass the Ramsay & USPS 955 Maintenance Exams First Try',
    indexable: true,
    priority: '1.0',
    changefreq: 'daily',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        SHARED_WEBSITE_NODE,
        SHARED_ORGANIZATION_NODE,
        {
          '@type': 'SoftwareApplication',
          '@id': `${CANONICAL_ORIGIN}/#app`,
          name: 'MaintenancePrep Test Simulator',
          applicationCategory: 'EducationalApplication',
          operatingSystem: 'Web, iOS, Android',
          url: `${CANONICAL_ORIGIN}/`,
          image: DEFAULT_OG_IMAGE,
          description:
            'Interactive 60-minute timed mock exam simulator and schematic troubleshooting platform for Ramsay Maintenance Assessments and USPS Exam 955.',
          offers: {
            '@type': 'Offer',
            price: '0.00',
            priceCurrency: 'USD',
            description: '14-day unrestricted free trial, then $9.99/month',
            url: `${CANONICAL_ORIGIN}/pricing`,
          },
        },
        {
          '@type': 'ItemList',
          '@id': `${CANONICAL_ORIGIN}/#tracks`,
          name: 'Ramsay & USPS 955 Maintenance Assessment Tracks',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Multicraft & USPS Postal Exam 955 Practice Track (MultiCrafTest)',
              url: `${CANONICAL_ORIGIN}/track/multicraft`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Electrical & Electronics Maintenance Track (Ramsay ELE-TEST)',
              url: `${CANONICAL_ORIGIN}/track/electrical`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'Mechanical Maintenance Track (Ramsay MEC-TEST)',
              url: `${CANONICAL_ORIGIN}/track/mechanical`,
            },
            {
              '@type': 'ListItem',
              position: 4,
              name: 'Mechanical Aptitude & Spatial Relations Track (Ramsay MAT & USPS 955 Part 2)',
              url: `${CANONICAL_ORIGIN}/track/aptitude`,
            },
            {
              '@type': 'ListItem',
              position: 5,
              name: 'Complete Industrial Electrical Schematics & Blueprint Reading Course',
              url: `${CANONICAL_ORIGIN}/course/schematics`,
            },
          ],
        },
        {
          '@type': 'FAQPage',
          '@id': `${CANONICAL_ORIGIN}/#faq`,
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is the Ramsay Maintenance Assessment and who takes it?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The Ramsay Maintenance Assessment is an industry-standard pre-hire and promotional screening exam published by Ramsay Corporation. Leading manufacturing, automotive, aerospace, and logistics companies administer Ramsay exams—such as MEC-TEST (Mechanical), ELE-TEST (Electrical), and MultiCrafTest—to evaluate candidate technical knowledge and troubleshooting ability under timed conditions.',
              },
            },
            {
              '@type': 'Question',
              name: 'Does MaintenancePrep cover the USPS Postal Exam 955?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes! The USPS Postal Exam 955 is the United States Postal Service primary examination for skilled maintenance craft careers: Maintenance Mechanic (MM-7), Mail Processing Equipment Mechanic (MPE-9), Building Equipment Mechanic (BEM-9), and Electronic Technician (ET-10), including Part 1 Technical/Multicraft knowledge and Part 2 Spatial Relations.',
              },
            },
            {
              '@type': 'Question',
              name: 'What is a passing score on Ramsay and USPS 955 exams?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Most manufacturing employers establish a 70% cutoff benchmark to pass Ramsay assessments. On the USPS Exam 955, scoring at least 70 qualifies you for placement on the register of eligible applicants, while scores of 80% to 90%+ receive the highest hiring and promotion priority.',
              },
            },
            {
              '@type': 'Question',
              name: 'Are the mock exams timed like the actual test?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. Full-length mock exams feature an active 60-minute countdown simulator that mirrors proctored assessment sessions (1 minute per question). Candidates can also practice in Untimed Study Mode with immediate logic proofs, circuit diagrams, and engineering formulas.',
              },
            },
            {
              '@type': 'Question',
              name: 'How does the 14-day free trial work?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Candidates receive full unrestricted access for 14 days to all 4 assessment tracks, unlimited timed mock exams, schematic diagrams, and diagnostic analytics for $0.00 today, then $9.99/month afterward. Cancel anytime with one click.',
              },
            },
          ],
        },
      ],
    },
    prerenderedHtml: `
      ${SHARED_NAV_HTML}
      <main>
        <section>
          <h1>Pass the Ramsay &amp; USPS Maintenance Exams on Your First Attempt.</h1>
          <p>High-yield practice exams, realistic circuit &amp; mechanical schematics, and rigorous 60-minute timed simulators built specifically for industrial maintenance technicians and USPS Exam 955 candidates (Maintenance Mechanic MM-7, MPE-9, BEM-9, and ET-10).</p>
        </section>
        <section>
          <h2>Select Your Assessment Track</h2>
          <ul>
            <li><a href="/track/multicraft"><strong>Multicraft &amp; USPS Exam 955 (MultiCrafTest)</strong> — PLC ladder logic, 3-phase motors, hydraulics, pneumatics, welding, and digital electronics.</a></li>
            <li><a href="/track/electrical"><strong>Electrical &amp; Electronics (Ramsay ELE-TEST)</strong> — AC/DC circuit diagnostics, motor control schematics, VFD drives, and NFPA 70E safety.</a></li>
            <li><a href="/track/mechanical"><strong>Mechanical Maintenance (Ramsay MEC-TEST)</strong> — Hydraulics, pneumatics, power transmission, bearings, pumps, and shaft alignment.</a></li>
            <li><a href="/track/aptitude"><strong>Mechanical Aptitude &amp; Spatial Relations (Ramsay MAT)</strong> — Gear trains, pulleys, levers, fluid mechanics, and 3D spatial folding.</a></li>
          </ul>
        </section>
        <section>
          <h2>Complete Electrical Schematics &amp; Blueprint Reading Course</h2>
          <p>24 illustrated lessons, 5 interactive diagnostic simulators, ANSI/IEEE/NEMA symbol dictionary, and a 30-question certification exam.</p>
          <p><a href="/course/schematics">Launch Electrical Schematics Course</a></p>
        </section>
        <section>
          <h2>Complete Preparation for the USPS Postal Exam 955</h2>
          <p>Covers Part 1 (Multicraft Technical, Motors, Hydraulics &amp; Schematics) and Part 2 (Spatial Relations &amp; 3D Mechanical Reasoning) for MM-7, MPE-9, BEM-9, and ET-10 roles.</p>
        </section>
      </main>
      ${SHARED_FOOTER_HTML}
    `,
  },

  '/track/multicraft': {
    path: '/track/multicraft',
    canonicalUrl: `${CANONICAL_ORIGIN}/track/multicraft`,
    title: 'Multicraft & USPS Postal Exam 955 Practice Test | Ramsay MultiCrafTest Prep',
    description:
      'Prepare for the Ramsay MultiCrafTest and USPS Postal Exam 955 (MM-7, MPE-9, BEM-9, ET-10) with 60-minute timed mock exams covering 3-phase motors, PLC ladder logic, hydraulics, pneumatics, welding, and mechanical power transmission.',
    keywords:
      'USPS Exam 955 practice test, Ramsay MultiCrafTest, multicraft maintenance test, Postal Exam 955 study guide, MPE-9 practice test, ET-10 electronic technician exam, BEM-9 building equipment mechanic, MM-7 maintenance mechanic, multicraft practice questions',
    robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'Multicraft & USPS Exam 955 Practice Track — MaintenancePrep',
    indexable: true,
    priority: '0.95',
    changefreq: 'weekly',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        SHARED_ORGANIZATION_NODE,
        createBreadcrumbNode([
          { name: 'Home', path: '/' },
          { name: 'Multicraft & USPS Exam 955 Track', path: '/track/multicraft' },
        ]),
        {
          '@type': 'Course',
          '@id': `${CANONICAL_ORIGIN}/track/multicraft#course`,
          name: 'Multicraft Maintenance & USPS Postal Exam 955 Preparation Track',
          description:
            'Comprehensive practice questions, schematic troubleshooting, and 60-minute timed mock exams for Ramsay MultiCrafTest and USPS Exam 955 (MM-7, MPE-9, BEM-9, ET-10).',
          provider: { '@id': `${CANONICAL_ORIGIN}/#organization` },
          inLanguage: 'en-US',
          hasCourseInstance: { '@type': 'CourseInstance', courseMode: 'online' },
        },
      ],
    },
    prerenderedHtml: `
      ${SHARED_NAV_HTML}
      <main>
        <h1>Multicraft Maintenance &amp; USPS Postal Exam 955 Assessment Track</h1>
        <p>Comprehensive preparation for Ramsay MultiCrafTest and USPS Exam 955 (MM-7 Maintenance Mechanic, MPE-9 Mail Processing Equipment Mechanic, BEM-9 Building Equipment Mechanic, and ET-10 Electronic Technician).</p>
        <h2>Core Assessment Modules</h2>
        <ul>
          <li>Industrial Electricity, 3-Phase Motors &amp; Control Circuits</li>
          <li>PLC Ladder Logic, Sensors (Sinking/Sourcing) &amp; Automation</li>
          <li>Hydraulics, Pneumatics &amp; Directional Control Schematics</li>
          <li>Mechanical Power Transmission, Bearings, Gears &amp; Alignment</li>
          <li>Welding, Piping, HVAC, Boilers &amp; OSHA / NFPA 70E Safety</li>
        </ul>
      </main>
      ${SHARED_FOOTER_HTML}
    `,
  },

  '/track/electrical': {
    path: '/track/electrical',
    canonicalUrl: `${CANONICAL_ORIGIN}/track/electrical`,
    title: 'Ramsay Electrical Test (ELE-TEST) Practice Exam | Industrial Electrician Prep',
    description:
      'Master the Ramsay Electrical Assessment (ELE-TEST) with realistic 60-minute timed mock exams covering AC/DC circuit analysis, 3-phase motor starters, VFD drives, PLC ladder logic, NEC/NFPA 70E safety, and digital electronics.',
    keywords:
      'Ramsay electrical test, Ramsay ELE-TEST practice, industrial electrician assessment, 3-phase motor control test, PLC troubleshooting exam, NFPA 70E maintenance test, electrical maintenance practice test',
    robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'Ramsay Electrical Test (ELE-TEST) Practice Track — MaintenancePrep',
    indexable: true,
    priority: '0.9',
    changefreq: 'weekly',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        SHARED_ORGANIZATION_NODE,
        createBreadcrumbNode([
          { name: 'Home', path: '/' },
          { name: 'Electrical & Electronics Track (ELE-TEST)', path: '/track/electrical' },
        ]),
        {
          '@type': 'Course',
          '@id': `${CANONICAL_ORIGIN}/track/electrical#course`,
          name: 'Ramsay Electrical & Electronics Maintenance (ELE-TEST) Preparation Track',
          description:
            'Timed mock exams and interactive schematics covering AC/DC theory, 3-phase motor control, transformers, VFDs, PLC ladder logic, and NEC/NFPA 70E electrical safety.',
          provider: { '@id': `${CANONICAL_ORIGIN}/#organization` },
          inLanguage: 'en-US',
          hasCourseInstance: { '@type': 'CourseInstance', courseMode: 'online' },
        },
      ],
    },
    prerenderedHtml: `
      ${SHARED_NAV_HTML}
      <main>
        <h1>Electrical &amp; Electronics Maintenance (Ramsay ELE-TEST) Assessment Track</h1>
        <p>Master industrial electrical troubleshooting, 3-phase motor controls, PLC ladder logic, VFD diagnostics, transformers, and NEC / NFPA 70E arc flash safety standards.</p>
        <h2>Core Electrical Assessment Modules</h2>
        <ul>
          <li>AC/DC Circuit Analysis, Ohm's Law &amp; Power Factor Calculations</li>
          <li>3-Phase Motors, Reversing Starters &amp; Overload Protection</li>
          <li>PLC Ladder Logic, Analog 4–20mA Loops &amp; Solid-State Relays</li>
          <li>Variable Frequency Drives (VFDs), Transformers &amp; Distribution</li>
          <li>Digital Logic Gates, Semiconductors &amp; Schematic Blueprint Reading</li>
        </ul>
        <p><a href="/course/schematics">Explore the Complete 24-Lesson Electrical Schematics &amp; Blueprint Reading Course</a></p>
      </main>
      ${SHARED_FOOTER_HTML}
    `,
  },

  '/track/mechanical': {
    path: '/track/mechanical',
    canonicalUrl: `${CANONICAL_ORIGIN}/track/mechanical`,
    title: 'Ramsay Mechanical Maintenance (MEC-TEST) Practice Exam | Millwright Prep',
    description:
      'Prepare for the Ramsay Mechanical Maintenance Test (MEC-TEST) with 60-minute timed mock exams and P&ID diagrams covering hydraulics, pneumatics, power transmission, gears, bearings, shaft alignment, centrifugal pumps, and rigging.',
    keywords:
      'Ramsay mechanical test, Ramsay MEC-TEST practice, industrial millwright test, mechanical maintenance assessment, hydraulics and pneumatics exam, power transmission test questions, bearing and pump troubleshooting',
    robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'Ramsay Mechanical Maintenance (MEC-TEST) Practice Track — MaintenancePrep',
    indexable: true,
    priority: '0.9',
    changefreq: 'weekly',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        SHARED_ORGANIZATION_NODE,
        createBreadcrumbNode([
          { name: 'Home', path: '/' },
          { name: 'Mechanical Maintenance Track (MEC-TEST)', path: '/track/mechanical' },
        ]),
        {
          '@type': 'Course',
          '@id': `${CANONICAL_ORIGIN}/track/mechanical#course`,
          name: 'Ramsay Mechanical Maintenance (MEC-TEST) & Millwright Preparation Track',
          description:
            'Timed mock simulations and P&ID troubleshooting covering hydraulics, pneumatics, mechanical drives, bearings, pumps, piping, welding, and precision measurement.',
          provider: { '@id': `${CANONICAL_ORIGIN}/#organization` },
          inLanguage: 'en-US',
          hasCourseInstance: { '@type': 'CourseInstance', courseMode: 'online' },
        },
      ],
    },
    prerenderedHtml: `
      ${SHARED_NAV_HTML}
      <main>
        <h1>Mechanical Maintenance (Ramsay MEC-TEST) Assessment Track</h1>
        <p>Rigorous practice exams and P&amp;ID schematics for industrial millwrights and mechanical technicians preparing for the Ramsay MEC-TEST assessment.</p>
        <h2>Core Mechanical Assessment Modules</h2>
        <ul>
          <li>Hydraulics &amp; Pneumatics (Directional Valves, Accumulators, Cylinder Force)</li>
          <li>Power Transmission (Gear Ratios, V-Belts, Roller Chains, Clutches &amp; Couplings)</li>
          <li>Bearings, Seals, Lubrication &amp; Laser/Dial Indicator Shaft Alignment</li>
          <li>Centrifugal &amp; Positive Displacement Pumps, Cavitation &amp; Affinity Laws</li>
          <li>Precision Measurement (Micrometers, Vernier Calipers), Rigging &amp; Welding</li>
        </ul>
      </main>
      ${SHARED_FOOTER_HTML}
    `,
  },

  '/track/aptitude': {
    path: '/track/aptitude',
    canonicalUrl: `${CANONICAL_ORIGIN}/track/aptitude`,
    title: 'Ramsay Mechanical Aptitude Test (MAT) & USPS 955 Spatial Relations Prep',
    description:
      'Practice for the Ramsay Mechanical Aptitude Test (MAT) and USPS Exam 955 Part 2 Spatial Relations with interactive pulley systems, gear trains, levers, fluid mechanics, tool identification, and 3D orthographic folding exercises.',
    keywords:
      'Ramsay mechanical aptitude test, Ramsay MAT practice test, USPS 955 spatial relations, mechanical reasoning test, gear and pulley test questions, pre-employment mechanical aptitude exam',
    robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'Ramsay Mechanical Aptitude Test (MAT) & Spatial Relations Track — MaintenancePrep',
    indexable: true,
    priority: '0.85',
    changefreq: 'weekly',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        SHARED_ORGANIZATION_NODE,
        createBreadcrumbNode([
          { name: 'Home', path: '/' },
          { name: 'Mechanical Aptitude & Spatial Track (MAT)', path: '/track/aptitude' },
        ]),
        {
          '@type': 'Course',
          '@id': `${CANONICAL_ORIGIN}/track/aptitude#course`,
          name: 'Ramsay Mechanical Aptitude (MAT) & USPS Exam 955 Spatial Relations Track',
          description:
            'Interactive practice covering mechanical advantage, block-and-tackle pulleys, gear rotation, levers, statics, acoustics, thermodynamics, and 3D spatial visualization.',
          provider: { '@id': `${CANONICAL_ORIGIN}/#organization` },
          inLanguage: 'en-US',
          hasCourseInstance: { '@type': 'CourseInstance', courseMode: 'online' },
        },
      ],
    },
    prerenderedHtml: `
      ${SHARED_NAV_HTML}
      <main>
        <h1>Mechanical Aptitude (Ramsay MAT) &amp; Spatial Relations Assessment Track</h1>
        <p>Build speed and accuracy on physical reasoning, mechanical advantage, and 3D spatial visualization tested on the Ramsay MAT and USPS Postal Exam 955 Part 2.</p>
        <h2>Core Aptitude &amp; Spatial Modules</h2>
        <ul>
          <li>Levers, Pulleys, Block-and-Tackle Mechanical Advantage &amp; Inclined Planes</li>
          <li>Gear Trains, Rack-and-Pinion Direction &amp; Belt Drive Kinematics</li>
          <li>Fluid Mechanics, Pascal's Law, Buoyancy &amp; Thermodynamics</li>
          <li>3D Spatial Visualization, Pattern Folding &amp; Orthographic Projections</li>
          <li>Shop Tool Identification, Fasteners &amp; Applied Shop Arithmetic</li>
        </ul>
      </main>
      ${SHARED_FOOTER_HTML}
    `,
  },

  '/course/schematics': {
    path: '/course/schematics',
    canonicalUrl: `${CANONICAL_ORIGIN}/course/schematics`,
    title: 'Industrial Electrical Schematics & Blueprint Reading Course | ANSI/IEEE/NEMA',
    description:
      'Interactive 24-lesson industrial electrical schematics and blueprint reading course covering ANSI/IEEE/NEMA symbols, ladder logic, multi-page signal tracing, semiconductors, op-amps, 5 diagnostic simulators, and a 30-question certification exam.',
    keywords:
      'electrical schematics course, how to read electrical schematics, industrial blueprint reading, ANSI IEEE schematic symbols, NEMA ladder logic diagrams, component-level troubleshooting course, electronic schematic symbols',
    robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'Complete Electrical Schematics & Blueprint Reading Course — MaintenancePrep',
    indexable: true,
    priority: '0.9',
    changefreq: 'weekly',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        SHARED_ORGANIZATION_NODE,
        createBreadcrumbNode([
          { name: 'Home', path: '/' },
          { name: 'Electrical Schematics & Blueprint Reading Course', path: '/course/schematics' },
        ]),
        {
          '@type': 'Course',
          '@id': `${CANONICAL_ORIGIN}/course/schematics#course`,
          name: 'Complete Industrial Electrical Schematics & Blueprint Reading Course',
          description:
            '24-lesson interactive curriculum covering ANSI/IEEE/NEMA schematic symbology, block & wiring diagrams, passive/active semiconductor components, op-amp filters, multi-page blueprint signal tracing, 5 interactive diagnostic calculators, and a 30-question final certification exam.',
          provider: { '@id': `${CANONICAL_ORIGIN}/#organization` },
          inLanguage: 'en-US',
          educationalLevel: 'Intermediate to Advanced Industrial Maintenance',
          hasCourseInstance: { '@type': 'CourseInstance', courseMode: 'online' },
        },
      ],
    },
    prerenderedHtml: `
      ${SHARED_NAV_HTML}
      <main>
        <h1>Complete Industrial Electrical Schematics &amp; Blueprint Reading Course</h1>
        <p>Master ANSI, IEEE, and NEMA schematic diagrams, component symbology, multi-page signal tracing, and systematic fault isolation with 24 illustrated lessons, 5 interactive simulators, and a 30-question final exam.</p>
        <h2>Curriculum Modules</h2>
        <ol>
          <li>Module 1: Schematic Fundamentals, Block Diagrams &amp; ANSI/IEEE Symbology</li>
          <li>Module 2: Passive Components, Switches, Relays, Solenoids &amp; Transformers</li>
          <li>Module 3: Semiconductors, Diodes, Transistors (BJT/MOSFET) &amp; Thyristors</li>
          <li>Module 4: Operational Amplifiers, Active Filters, Optoelectronics &amp; Digital Logic</li>
          <li>Module 5: Power Supplies, Voltage Regulation &amp; Inverter Topologies</li>
          <li>Module 6: Multi-Sheet Blueprint Signal Tracing &amp; Half-Splitting Troubleshooting</li>
        </ol>
      </main>
      ${SHARED_FOOTER_HTML}
    `,
  },

  '/study-lab': {
    path: '/study-lab',
    canonicalUrl: `${CANONICAL_ORIGIN}/study-lab`,
    title: 'Industrial Maintenance Study Lab | Formula Calculators, Schematics & Flashcards',
    description:
      'Interactive industrial maintenance study lab featuring 3-phase motor & hydraulic cylinder force calculators, PLC sinking vs. sourcing guides, pump affinity laws, and high-yield flashcards for Ramsay and USPS Exam 955 prep.',
    keywords:
      'industrial maintenance formulas, 3-phase power calculator, hydraulic cylinder force calculator, maintenance flashcards, PLC sinking vs sourcing, centrifugal pump affinity laws, synchronous motor speed formula',
    robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'Industrial Maintenance Study Lab & Interactive Calculators — MaintenancePrep',
    indexable: true,
    priority: '0.85',
    changefreq: 'weekly',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        SHARED_ORGANIZATION_NODE,
        createBreadcrumbNode([
          { name: 'Home', path: '/' },
          { name: 'Interactive Study Lab & Formula Calculators', path: '/study-lab' },
        ]),
        {
          '@type': 'LearningResource',
          '@id': `${CANONICAL_ORIGIN}/study-lab#resource`,
          name: 'Industrial Maintenance Interactive Study Lab, Engineering Calculators & Flashcards',
          description:
            'Interactive study flashcards, engineering formula calculators (Ohm’s Law, 3-Phase Power, Motor Synchronous Speed, Hydraulic Cylinder Force, Shaft Torque, Pump Affinity Laws), and schematic reference sheets.',
          provider: { '@id': `${CANONICAL_ORIGIN}/#organization` },
          learningResourceType: ['Flashcards', 'Interactive Simulation', 'Formula Reference'],
          inLanguage: 'en-US',
        },
      ],
    },
    prerenderedHtml: `
      ${SHARED_NAV_HTML}
      <main>
        <h1>Interactive Maintenance Study Lab, Formula Calculators &amp; Flashcards</h1>
        <p>Master essential electrical, mechanical, hydraulic, and safety formulas tested on Ramsay Maintenance Assessments and the USPS Postal Exam 955.</p>
        <h2>Key Industrial Maintenance Formulas</h2>
        <ul>
          <li><strong>Synchronous Motor Speed:</strong> Ns = (120 × f) / p</li>
          <li><strong>3-Phase AC Real Power:</strong> P = √3 × V_LL × I_L × PF</li>
          <li><strong>Hydraulic Cylinder Force:</strong> F = P × A = P × (π × d² / 4)</li>
          <li><strong>Shaft Torque from Horsepower:</strong> T = (5,252 × HP) / RPM</li>
          <li><strong>Centrifugal Pump Affinity Laws:</strong> Q ∝ N, H ∝ N², P ∝ N³</li>
        </ul>
      </main>
      ${SHARED_FOOTER_HTML}
    `,
  },

  '/pricing': {
    path: '/pricing',
    canonicalUrl: `${CANONICAL_ORIGIN}/pricing`,
    title: 'Pricing & 14-Day Free Trial | MaintenancePrep Pro',
    description:
      'Start your 14-day free trial of MaintenancePrep Pro ($0.00 today, then $9.99/month). Get unlimited 60-minute timed mock exams, all 4 Ramsay & USPS 955 tracks, interactive schematics, and readiness analytics.',
    keywords:
      'MaintenancePrep pricing, Ramsay test prep free trial, USPS 955 practice test subscription, maintenance exam simulator cost',
    robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'MaintenancePrep Pro — 14-Day Free Trial Pricing',
    indexable: true,
    priority: '0.8',
    changefreq: 'weekly',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        SHARED_ORGANIZATION_NODE,
        createBreadcrumbNode([
          { name: 'Home', path: '/' },
          { name: 'Pricing & 14-Day Free Trial', path: '/pricing' },
        ]),
        {
          '@type': 'Product',
          '@id': `${CANONICAL_ORIGIN}/pricing#product`,
          name: 'MaintenancePrep Pro Monthly Membership',
          description:
            'Unlimited 60-minute timed mock exams, all 4 Ramsay & USPS 955 assessment tracks, high-resolution electrical & hydraulic schematics, and diagnostic readiness analytics.',
          brand: { '@type': 'Brand', name: 'MaintenancePrep' },
          image: DEFAULT_OG_IMAGE,
          offers: {
            '@type': 'Offer',
            url: `${CANONICAL_ORIGIN}/pricing`,
            price: '9.99',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            description: '14-day free trial ($0.00 today), then $9.99/month. Cancel anytime.',
          },
        },
      ],
    },
    prerenderedHtml: `
      ${SHARED_NAV_HTML}
      <main>
        <h1>Invest in Your Career with MaintenancePrep Pro</h1>
        <p>Start with a 14-day free trial ($0.00 today, then $9.99/month). Experience unlimited realistic 60-minute mock simulations, schematic diagrams, and verified engineering solutions. Cancel anytime.</p>
        <h2>Monthly Pro Membership Includes</h2>
        <ul>
          <li>Unlimited 60-Minute Real Timed Mock Exams</li>
          <li>All 4 Tracks: Mechanical (MEC-TEST), Electrical (ELE-TEST), Multicraft &amp; USPS 955, and Aptitude (MAT)</li>
          <li>Complete 24-Lesson Industrial Electrical Schematics &amp; Blueprint Reading Course</li>
          <li>Step-by-Step Engineering Formulas, Calculations &amp; Logic Proofs</li>
        </ul>
      </main>
      ${SHARED_FOOTER_HTML}
    `,
  },

  '/dashboard': {
    path: '/dashboard',
    canonicalUrl: `${CANONICAL_ORIGIN}/dashboard`,
    title: 'Candidate Dashboard | MaintenancePrep',
    description:
      'Review your Ramsay and USPS Exam 955 mock exam scores, domain readiness analytics, and study progress.',
    keywords: 'MaintenancePrep dashboard, candidate test analytics',
    robots: 'noindex, nofollow',
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'MaintenancePrep Candidate Dashboard',
    indexable: false,
    priority: '0.3',
    changefreq: 'monthly',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Candidate Dashboard | MaintenancePrep',
      url: `${CANONICAL_ORIGIN}/dashboard`,
    },
    prerenderedHtml: '',
  },

  '/exam': {
    path: '/exam',
    canonicalUrl: `${CANONICAL_ORIGIN}/exam`,
    title: 'Active Assessment Simulator | MaintenancePrep',
    description:
      'Interactive 60-minute timed mock exam and untimed study simulator for Ramsay and USPS Exam 955 maintenance assessments.',
    keywords: 'MaintenancePrep exam simulator',
    robots: 'noindex, nofollow',
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'MaintenancePrep Exam Simulator',
    indexable: false,
    priority: '0.3',
    changefreq: 'monthly',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Active Assessment Simulator | MaintenancePrep',
      url: `${CANONICAL_ORIGIN}/exam`,
    },
    prerenderedHtml: '',
  },
};

export function getSeoForPath(rawPath: string): SeoRouteConfig {
  const normalized = normalizeSeoPath(rawPath);
  return SEO_ROUTES[normalized] || SEO_ROUTES['/'];
}

export function getIndexableRoutes(): SeoRouteConfig[] {
  return Object.values(SEO_ROUTES).filter((route) => route.indexable);
}

export function generateSitemapXml(): string {
  const routes = getIndexableRoutes();
  const urlEntries = routes
    .map(
      (route) => `  <url>
    <loc>${route.canonicalUrl}</loc>
    <lastmod>${LAST_MODIFIED_DATE}</lastmod>
    <changefreq>${route.changefreq}</changefreq>
    <priority>${route.priority}</priority>
  </url>`
    )
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urlEntries}
</urlset>
`;
}

export function generateRobotsTxt(): string {
  return `User-agent: *
Allow: /
Disallow: /api/
Disallow: /dashboard
Disallow: /exam

Sitemap: ${CANONICAL_ORIGIN}/sitemap.xml
`;
}

function escapeHtmlAttr(val: string): string {
  return val
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Replaces head metadata, canonical tags, Open Graph / Twitter tags, JSON-LD,
 * and optional pre-rendered semantic fallback HTML inside a base index.html string.
 */
export function injectSeoIntoHtml(baseHtml: string, rawPath: string): string {
  const seo = getSeoForPath(rawPath);
  const safeTitle = escapeHtmlAttr(seo.title);
  const safeDesc = escapeHtmlAttr(seo.description);
  const safeKeywords = escapeHtmlAttr(seo.keywords);
  const safeCanonical = escapeHtmlAttr(seo.canonicalUrl);
  const safeOgImage = escapeHtmlAttr(seo.ogImage);
  const safeOgImageAlt = escapeHtmlAttr(seo.ogImageAlt);

  let html = baseHtml;

  // Replace <title>
  html = html.replace(/<title>[\s\S]*?<\/title>/i, `<title>${safeTitle}</title>`);

  // Replace <meta name="description">
  html = html.replace(
    /<meta\s+name="description"\s+content="[^"]*"\s*\/?>/i,
    `<meta name="description" content="${safeDesc}" />`
  );

  // Replace <meta name="keywords">
  html = html.replace(
    /<meta\s+name="keywords"\s+content="[^"]*"\s*\/?>/i,
    `<meta name="keywords" content="${safeKeywords}" />`
  );

  // Replace <meta name="robots">
  html = html.replace(
    /<meta\s+name="robots"\s+content="[^"]*"\s*\/?>/i,
    `<meta name="robots" content="${escapeHtmlAttr(seo.robots)}" />`
  );

  // Replace <link rel="canonical">
  html = html.replace(
    /<link\s+rel="canonical"\s+href="[^"]*"\s*\/?>/i,
    `<link rel="canonical" href="${safeCanonical}" />`
  );

  // Replace Open Graph tags
  html = html.replace(
    /<meta\s+property="og:type"\s+content="[^"]*"\s*\/?>/i,
    `<meta property="og:type" content="${seo.ogType}" />`
  );
  html = html.replace(
    /<meta\s+property="og:url"\s+content="[^"]*"\s*\/?>/i,
    `<meta property="og:url" content="${safeCanonical}" />`
  );
  html = html.replace(
    /<meta\s+property="og:title"\s+content="[^"]*"\s*\/?>/i,
    `<meta property="og:title" content="${safeTitle}" />`
  );
  html = html.replace(
    /<meta\s+property="og:description"\s+content="[^"]*"\s*\/?>/i,
    `<meta property="og:description" content="${safeDesc}" />`
  );
  html = html.replace(
    /<meta\s+property="og:image"\s+content="[^"]*"\s*\/?>/i,
    `<meta property="og:image" content="${safeOgImage}" />`
  );
  html = html.replace(
    /<meta\s+property="og:image:alt"\s+content="[^"]*"\s*\/?>/i,
    `<meta property="og:image:alt" content="${safeOgImageAlt}" />`
  );

  // Replace Twitter tags
  html = html.replace(
    /<meta\s+name="twitter:title"\s+content="[^"]*"\s*\/?>/i,
    `<meta name="twitter:title" content="${safeTitle}" />`
  );
  html = html.replace(
    /<meta\s+name="twitter:description"\s+content="[^"]*"\s*\/?>/i,
    `<meta name="twitter:description" content="${safeDesc}" />`
  );
  html = html.replace(
    /<meta\s+name="twitter:image"\s+content="[^"]*"\s*\/?>/i,
    `<meta name="twitter:image" content="${safeOgImage}" />`
  );
  html = html.replace(
    /<meta\s+name="twitter:image:alt"\s+content="[^"]*"\s*\/?>/i,
    `<meta name="twitter:image:alt" content="${safeOgImageAlt}" />`
  );

  // Replace JSON-LD block
  const jsonLdString = JSON.stringify(seo.jsonLd, null, 2);
  html = html.replace(
    /<script[^>]*type="application\/ld\+json"[^>]*>[\s\S]*?<\/script>/i,
    `<script id="seo-json-ld" type="application/ld+json">\n${jsonLdString}\n    </script>`
  );

  // Inject pre-rendered semantic HTML inside <div id="root">...</div> for crawlers
  if (seo.prerenderedHtml) {
    html = html.replace(
      /<div id="root"><\/div>/i,
      `<div id="root"><div class="sr-only">${seo.prerenderedHtml.trim()}</div></div>`
    );
  }

  return html;
}
